import axios from "axios";

const getBaseUrl = () => {
  return (
    process.env.REACT_APP_CHAT_MICROSERVICE_URL ||
    "http://localhost:8000/api/v1"
  );
};

export const kbService = {
  // Get public collections for partner
  getCollections: async (partnerId) => {
    if (!partnerId) return [];
    try {
      const response = await axios.get(
        `${getBaseUrl()}/knowledgebase/collections-public/${partnerId}`
      );
      return response.data?.data || response.data || [];
    } catch (err) {
      console.error("Error fetching collections:", err);
      return [];
    }
  },

  // Get all published articles for partner
  getPublishedArticles: async (partnerId) => {
    if (!partnerId) return [];
    try {
      const response = await axios.get(
        `${getBaseUrl()}/knowledgebase/article-published-public/${partnerId}`
      );
      return response.data?.data || response.data || [];
    } catch (err) {
      console.error("Error fetching published articles:", err);
      return [];
    }
  },

  // Search articles
  searchArticles: async (partnerId, query) => {
    if (!partnerId || !query || query.trim().length < 2) return [];
    try {
      const response = await axios.get(
        `${getBaseUrl()}/knowledgebase/article/${partnerId}/search`,
        {
          params: { query: query.trim() },
        }
      );
      return response.data?.data || response.data || [];
    } catch (err) {
      console.error("Error searching articles:", err);
      return [];
    }
  },

  // Get most viewed articles
  getMostViewedArticles: async (partnerId, limit = 6) => {
    if (!partnerId) return [];
    try {
      const response = await axios.get(
        `${getBaseUrl()}/knowledgebase/most-viewed/${partnerId}`,
        {
          params: { limit },
        }
      );
      return response.data?.data || response.data || [];
    } catch (err) {
      console.error("Error fetching most-viewed articles:", err);
      return [];
    }
  },

  // Get featured articles
  getFeaturedArticles: async (partnerId) => {
    if (!partnerId) return [];
    try {
      const response = await axios.get(
        `${getBaseUrl()}/knowledgebase/featured/${partnerId}`
      );
      return response.data?.data || response.data || [];
    } catch (err) {
      console.error("Error fetching featured articles:", err);
      return [];
    }
  },

  // Get article by ID
  getArticleById: async (articleId) => {
    if (!articleId) return null;
    try {
      const response = await axios.get(
        `${getBaseUrl()}/knowledgebase/article-by-id/${articleId}`
      );
      return response.data?.data || response.data || null;
    } catch (err) {
      console.error("Error fetching article by ID:", err);
      return null;
    }
  },

  // Get article by slug
  getArticleBySlug: async (partnerId, slug) => {
    if (!slug) return null;
    try {
      const response = await axios.get(
        `${getBaseUrl()}/knowledgebase/article/slug/${slug}`,
        {
          params: { partnerId },
        }
      );
      return response.data?.data || response.data || null;
    } catch (err) {
      console.error("Error fetching article by slug:", err);
      return null;
    }
  },

  // Track article view
  trackView: async (articleId, partnerId) => {
    if (!articleId || !partnerId) return;
    try {
      await axios.post(
        `${getBaseUrl()}/knowledgebase-setting/${articleId}/view`,
        { partnerId }
      );
    } catch (err) {
      console.warn("View tracking failed:", err?.message);
    }
  },

  // Submit article reaction
  addReaction: async (articleId, partnerId, type, feedback = "") => {
    if (!articleId || !partnerId || !type) return null;
    try {
      const response = await axios.post(
        `${getBaseUrl()}/knowledgebase-setting/${articleId}/reaction`,
        { type, partnerId, feedback }
      );
      return response.data;
    } catch (err) {
      console.error("Error submitting reaction:", err);
      return null;
    }
  },

  // Get announcement / news articles
  getAnnouncements: async (partnerId) => {
    if (!partnerId) return [];
    try {
      const sortNews = (items) => {
        return items.sort((a, b) => {
          if (Boolean(b.isFeatured) !== Boolean(a.isFeatured)) {
            return b.isFeatured ? 1 : -1;
          }
          const dateB = new Date(b.updatedAt || b.DateofUpdation || b.createdAt || 0).getTime();
          const dateA = new Date(a.updatedAt || a.DateofUpdation || a.createdAt || 0).getTime();
          return dateB - dateA;
        });
      };

      // 1. Try dedicated announcements endpoint first
      try {
        const annRes = await axios.get(
          `${getBaseUrl()}/knowledgebase/announcements/${partnerId}`
        );
        const data = annRes.data?.data || annRes.data;
        if (Array.isArray(data) && data.length > 0) {
          return sortNews(data);
        }
      } catch (e) {
        // fallback to query
      }

      // 2. Query with articleType parameter
      const response = await axios.get(
        `${getBaseUrl()}/knowledgebase/article-published-public/${partnerId}`,
        { params: { articleType: 'news,announcement' } }
      );
      const allArticles = response.data?.data || response.data || [];
      const announcements = allArticles.filter(
        (art) => art.articleType === "announcement" || art.articleType === "news"
      );
      return sortNews(announcements);
    } catch (err) {
      console.error("Error fetching announcements:", err);
      return [];
    }
  },

  // Get latest announcement
  getLatestAnnouncement: async (partnerId) => {
    if (!partnerId) return null;
    try {
      const announcements = await kbService.getAnnouncements(partnerId);
      return announcements.length > 0 ? announcements[0] : null;
    } catch (err) {
      console.error("Error fetching latest announcement:", err);
      return null;
    }
  },

  // Get active in-app popups for current visitor
  getActivePopups: async (partnerId, { email = "", url = "" } = {}) => {
    if (!partnerId) return [];
    try {
      const response = await axios.get(
        `${getBaseUrl()}/knowledgebase/popups/active/${partnerId}`,
        {
          params: { email, url },
        }
      );
      return response.data?.data || [];
    } catch (err) {
      console.error("Error fetching active popups:", err);
      return [];
    }
  },

  // Track popup interaction (impression, click, dismissal)
  trackPopupInteraction: async (articleId, interaction, email = "") => {
    if (!articleId || !interaction) return;
    try {
      await axios.post(
        `${getBaseUrl()}/knowledgebase/popups/interaction/${articleId}`,
        { interaction, email }
      );
    } catch (err) {
      console.warn("Popup interaction tracking failed:", err?.message);
    }
  },
};

export default kbService;
